import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Select from "components/common/Select";
import EventService from "service/event/EventService";
import PatientService from "../../service/PatientService";
import {
  buildProfileUpdateBody,
  NAME_MAX,
  nameError,
  nameErrors,
  nameFilter,
} from "../../components/PatientDetailsDialog";
import { PD_V2_REFRESH } from "./events";

/**
 * Patient details editor form — the SECTION-rail version.
 *
 * Sibling to PatientDetailsDialog (which still owns the header-CTA
 * popup — dual entry point per product ruling). The wire body, the
 * required-fields gate, and the save endpoint are all shared: this
 * form imports `buildProfileUpdateBody` straight from the dialog
 * module and calls `PatientService.updatePatientProfile` the same
 * way, so the two entry points can never drift.
 *
 * Five editable fields (First name*, Last name, Gender*, Date of
 * birth*, Communication email) — Mobile number and Work email are
 * NEVER edited here either (see the dialog file's header for the
 * "why"). They show muted on the middle-column table and don't
 * exist on the form at all — the wire body echoes them back
 * unchanged via the shared buildProfileUpdateBody helper.
 *
 * Focus targeting: the middle-column table emits PD_V2_EDIT with a
 * `focusField` (or none for a read-only row). The Editor unpacks it
 * onto this component's `focusField` prop; on mount we move the
 * caret into that input so a click on "Gender" in the table lands
 * the reviewer in the gender picker without a mouse trip.
 */

const GENDERS = [
  { key: 2, label: "Male" },
  { key: 1, label: "Female" },
];

const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const asText = (v) => (v === null || v === undefined ? "" : String(v));

const REQUIRED = {
  firstName: (form) => !!asText(form.firstName).trim(),
  gender: (form) => GENDERS.some((g) => g.key === form.gender),
  dateOfBirth: (form) => !!form.dateOfBirth,
};

/* The gender Select's trigger id — the form's focus map and its <label> both
   point at it, so clicking the label focuses the control too. */
const GENDER_ID = "pdx-gender";

const PatientDetailsForm = ({ patient, focusField, focusAt, onClose, onSaved, onManualSave, themeClass }) => {
  // Transcript-mode mounts pass `onManualSave` so this form stages
  // through /captured instead of hitting bridge directly. Standalone
  // mounts leave it null and the existing PatientService PUT runs.
  const isTranscript = typeof onManualSave === "function";

  const initial = useMemo(
    () => ({
      firstName: asText(patient.firstName),
      lastName: asText(patient.lastName),
      phoneNo: asText(patient.phoneno),
      gender: patient.gender,
      dateOfBirth: toDateInput(patient.dateOfBirth),
      email: asText(patient.email),
    }),
    [patient]
  );

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  /* The name rules are the patient app's, and they live with the header
     dialog's copy of this form (PatientDetailsDialog) for the same reason the
     wire body does: two entry points onto one record cannot disagree about
     what a name is. */
  const [nameErr, setNameErr] = useState({});

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Focus target — set by the table's row click. rAF so the input
  // exists before .focus() runs (form mounts + steppers/select
  // render inside their own effects).
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const dobRef = useRef(null);
  const emailRef = useRef(null);
  useEffect(() => {
    if (!focusField) return;
    const raf = window.requestAnimationFrame || ((cb) => setTimeout(cb, 0));
    raf(() => {
      const map = {
        firstName: firstNameRef.current,
        lastName: lastNameRef.current,
        dateOfBirth: dobRef.current,
        email: emailRef.current,
        /* GENDER IS A CUSTOM SELECT, and it used to be the one row that opened
           the rail and focused nothing — so the reviewer had to go and click
           the field the click was about (Nittin, 2026-09-24). It is reached by
           the id the Select puts on its own trigger rather than by a ref,
           because that component is a host file the two repos have already
           diverged on and a focused BUTTON is what a native select would give
           here anyway: Enter, Space and the arrows all open it from there. */
        gender: document.getElementById(GENDER_ID),
      };
      const el = map[focusField];
      if (el && typeof el.focus === "function") {
        try { el.focus({ preventScroll: false }); } catch (_) { /* best-effort */ }
      }
    });
    /* `focusAt` is what makes a SECOND click on the same row work: the field
       asked for has not changed, so without it this effect would not re-run
       and the rail — which stays open between rows — would sit there focused
       on whatever was last touched. */
  }, [focusField, focusAt]);

  const set = (k) => (v) => {
    /* Names are FILTERED as they are typed, the way the app filters them. */
    const next = k === "firstName" || k === "lastName" ? nameFilter(k, v) : v;
    setForm((prev) => ({ ...prev, [k]: next }));
    if (k === "firstName" || k === "lastName") {
      setNameErr((prev) => (prev[k] && !nameError(k, next) ? { ...prev, [k]: "" } : prev));
    }
  };

  const dirty = useMemo(
    () => Object.keys(initial).some((k) => form[k] !== initial[k]),
    [form, initial]
  );
  const complete = Object.keys(REQUIRED).every((k) => REQUIRED[k](form));
  const canSave = dirty && !submitting && complete;

  const submit = () => {
    if (!canSave) return;
    /* Checked on the press, and said out loud — not by greying Save out. */
    const bad = nameErrors(form);
    setNameErr(bad);
    if (Object.keys(bad).length) return;
    setSubmitting(true);
    setFeedback(null);

    const body = buildProfileUpdateBody(patient, form);

    // Transcript mode — hand the body off to the caller for /captured
    // staging. No bridge PUT here; the reviewer commits later via
    // Save & next. Optimistic close: transcript captures are lossless
    // (idempotent uniqueKey upsert on the Go side), so we don't wait
    // for a network hop before dropping the rail.
    if (isTranscript) {
      try {
        onManualSave(body, {
          onSuccess: () => {
            if (!mounted.current) return;
            setSubmitting(false);
            if (onSaved) onSaved({ ...patient, ...form, phoneno: form.phoneNo });
          },
          onError: (err) => {
            if (!mounted.current) return;
            setSubmitting(false);
            const message = (err && err.message) || "Couldn't stage the update — please retry";
            setFeedback(message);
            EventService.emit("showError", { message: `Couldn't stage patient details: ${message}` });
          },
        });
      } catch (e) {
        if (!mounted.current) return;
        setSubmitting(false);
        const message = (e && e.message) || "Couldn't stage the update — please retry";
        setFeedback(message);
      }
      return;
    }

    // Standalone — direct PUT to bridge.
    new PatientService().updatePatientProfile(
      body,
      (res) => {
        if (!mounted.current) return;
        setSubmitting(false);
        // Success toast intentionally NOT emitted here — the section
        // rail closing + the table re-rendering with the new values
        // is the visible confirmation. Error toast still fires below
        // because a failure has no other on-screen signal.
        //
        // Emit the freshly-saved patient object as the event payload
        // so the PatientDetails table's PD_V2_REFRESH listener has
        // something to re-seed its local `live` state from. Emitting
        // without a payload left `fresh` undefined on the listener
        // side, so the table only updated on a full page reload.
        const fresh = { ...patient, ...form, phoneno: form.phoneNo };
        EventService.emit(PD_V2_REFRESH, fresh);
        if (onSaved) onSaved(fresh, res);
      },
      (err) => {
        if (!mounted.current) return;
        setSubmitting(false);
        const message = (err && err.message) || "Couldn't save — please retry";
        setFeedback(message);
        EventService.emit("showError", { message: `Couldn't save patient details: ${message}` });
      }
    );
  };

  return (
    <div
      className={`pd-form cc3-form${themeClass ? ` ${themeClass}` : ""}`}
      role="group"
      aria-label="Update patient details"
    >
      <header className="pd-form-head">
        <h3 className="pd-form-title">Update</h3>
      </header>

      <div className="pd-form-body">
        <div className="pdx-field">
          <label className="pdx-lbl">
            First name<span className="pdx-req" aria-hidden="true">*</span>
          </label>
          <input
            ref={firstNameRef}
            className="pdx-input"
            type="text"
            value={form.firstName}
            onChange={(e) => set("firstName")(e.target.value)}
            disabled={submitting}
            aria-label="First name"
            aria-required="true"
            aria-invalid={!!nameErr.firstName}
            maxLength={NAME_MAX}
          />
          {nameErr.firstName ? <p className="pdx-err pdx-field-err">{nameErr.firstName}</p> : null}
        </div>

        <div className="pdx-field">
          <label className="pdx-lbl">Last name</label>
          <input
            ref={lastNameRef}
            className="pdx-input"
            type="text"
            value={form.lastName}
            onChange={(e) => set("lastName")(e.target.value)}
            disabled={submitting}
            aria-label="Last name"
            aria-invalid={!!nameErr.lastName}
            maxLength={NAME_MAX}
          />
          {nameErr.lastName ? <p className="pdx-err pdx-field-err">{nameErr.lastName}</p> : null}
        </div>

        <div className="pdx-field">
          <label className="pdx-lbl" htmlFor={GENDER_ID}>
            Gender<span className="pdx-req" aria-hidden="true">*</span>
          </label>
          <Select
            id={GENDER_ID}
            value={form.gender}
            options={GENDERS}
            getKey={(o) => o.key}
            getLabel={(o) => o.label}
            onChange={set("gender")}
            disabled={submitting}
            /* `ait-menu` gets the transcript modal's z-index bump so
               the portalled menu opens ABOVE the modal overlay
               (z-index 1500). Without it the menu opened behind, and
               every option click landed on the backdrop. Standalone
               mounts are unaffected — no overlay competes on the
               patient page. */
            menuClassName="pdx-menu ait-menu"
          />
        </div>

        <div className="pdx-field">
          <label className="pdx-lbl">
            Date of birth<span className="pdx-req" aria-hidden="true">*</span>
          </label>
          <div className="pdx-date">
            <input
              ref={dobRef}
              className="pdx-input"
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => set("dateOfBirth")(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              /* Native picker won't let the reviewer scroll below Jan
                 1900 — same guard the header dialog uses. Blocks a
                 fat-finger '0202-06-…' from landing as a real,
                 distant-past date. */
              min="1900-01-01"
              disabled={submitting}
              aria-label="Date of birth"
              aria-required="true"
            />
            {/* Calendar icon — mirrors the dashboard dialog's date field
                (native <input type=date> paints its picker button on the
                right, but Chrome renders it very small; the icon gives a
                visible affordance at the same position). */}
            <span className="pdx-date-icon" aria-hidden="true">
              {/* viewBox reaches y=16 to include the path's full
                  extent (draws down to 15.625). The dashboard dialog
                  gets away with `0 0 15 15` because its icon renders
                  larger and the sub-pixel clip isn't noticeable
                  there; at 16px in this rail, the bottom row of the
                  calendar visibly cropped. */}
              <svg viewBox="0 0 15 16" fill="none">
                <path
                  d="M12.1875 3.125H11.5625V1.875H10.3125V3.125H4.0625V1.875H2.8125V3.125H2.1875C1.5 3.125 0.9375 3.6875 0.9375 4.375V14.375C0.9375 15.0625 1.5 15.625 2.1875 15.625H12.1875C12.875 15.625 13.4375 15.0625 13.4375 14.375V4.375C13.4375 3.6875 12.875 3.125 12.1875 3.125ZM12.1875 14.375H2.1875V7.5H12.1875V14.375ZM12.1875 6.25H2.1875V4.375H12.1875V6.25Z"
                  fill="currentColor"
                />
              </svg>
            </span>
          </div>
        </div>

        <div className="pdx-field">
          <label className="pdx-lbl">Communication email</label>
          <input
            ref={emailRef}
            className="pdx-input"
            type="email"
            value={form.email}
            onChange={(e) => set("email")(e.target.value)}
            disabled={submitting}
            aria-label="Communication email"
          />
        </div>

        {feedback && <p className="pdx-err">{feedback}</p>}
      </div>

      <footer className="pd-form-foot">
        <button type="button" className="pdx-cancel" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className={`pdx-save${themeClass ? ` ${themeClass}` : ""}`}
          onClick={submit}
          disabled={!canSave}
        >
          {submitting
            ? (isTranscript ? "Adding…" : "Saving…")
            : (isTranscript ? "Add" : "Save")}
        </button>
      </footer>
    </div>
  );
};

PatientDetailsForm.propTypes = {
  patient: PropTypes.object.isRequired,
  focusField: PropTypes.string,
  /** Changes on every row click; see the focus effect. */
  focusAt: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  onManualSave: PropTypes.func,
  /** "is-ai" for AI-authored staged captures (maroon border + save
   *  button); "is-manual" for reviewer-opened cards (default indigo);
   *  falsy for the standalone dashboard dialog. */
  themeClass: PropTypes.string,
};

PatientDetailsForm.defaultProps = {
  focusField: null,
  focusAt: 0,
  onSaved: null,
  onManualSave: null,
  themeClass: "",
};

export default PatientDetailsForm;
